import React, { useEffect, useRef } from 'react';
import { formatCurrency } from '../../hooks/useBonusesData';

// Palette to match social charts
const colorPalette = {
  VM: '#96e6ab',
  EJ: '#76ace0',
  WBG: '#df87a8',
  W2J: '#f4d353',
};
const fallbackColors = [
  '#b3e2cd',
  '#fdcdac',
  '#cbd5e8',
  '#f4cae4',
  '#e6f5c9',
  '#fff2ae',
  '#f1e2cc',
  '#cccccc',
  '#8dd3c7',
  '#ffffb3',
];
const getColor = (index) => {
  const colors = Object.values(colorPalette).concat(fallbackColors);
  return colors[index % colors.length];
};

function TopBonusesBar({ d3, data }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!d3 || !containerRef.current || !data?.length) return;

    const container = containerRef.current;
    container.innerHTML = '';

    const width = 600;
    const height = 600;
    const margin = { top: 36, right: 40, bottom: 50, left: 20 };

    const svg = d3
      .select(container)
      .append('svg')
      .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto')
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', -10)
      .attr('text-anchor', 'middle')
      .style('font-size', '16px')
      .style('font-weight', 600)


    const x = d3
      .scaleLinear()
      .domain([0, d3.max(data, (d) => d.amount)])
      .nice()
      .range([0, width]);

    const y = d3
      .scaleBand()
      .domain(data.map((d) => d.label))
      .range([0, height])
      .padding(0.1);

    svg
      .append('g')
      .attr('transform', `translate(0, ${height})`)
      .call(d3.axisBottom(x).ticks(5).tickFormat((d) => `$${d3.format(',')(d)}`))
      .selectAll('text')
      .style('font-size', '16px')
      .style('fill', '#444');

    svg
      .append('g')
      .call(d3.axisLeft(y).tickFormat(''))
      .selectAll('text')
      .remove();

    svg
      .append('line')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', height)
      .attr('stroke', '#ccc');

    const bars = svg
      .selectAll('rect')
      .data(data)
      .enter()
      .append('rect')
      .attr('x', 0)
      .attr('y', (d) => y(d.label))
      .attr('width', (d) => x(d.amount))
      .attr('height', y.bandwidth())
      .attr('fill', (_, i) => getColor(i));

    bars
      .append('title')
      .text((d) => `${d.label}: ${formatCurrency(d.amount)}`);

    const insideThreshold = width * 0.7;
    const labelFontPx = 16;

    svg
      .selectAll('text.value-label')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'value-label')
      .attr('x', (d) => {
        const barX = x(d.amount);
        if (barX >= insideThreshold) {
          return barX - 10;
        }
        return barX + 10;
      })
      .attr('y', (d) => y(d.label) + y.bandwidth() / 2)
      .attr('dominant-baseline', 'middle')
      .attr('text-anchor', (d) => (x(d.amount) >= insideThreshold ? 'end' : 'start'))
      .style('font-size', `${labelFontPx}px`)
      .style('fill', '#444')
      .text((d) => `${d.label} (${formatCurrency(d.amount)})`);
  }, [d3, data]);

  return (
    <div className="chart-container">
      <h2 style={{ textAlign: 'left' }}>TOP BONUSES BY AMOUNT WON</h2>
      <div ref={containerRef} />
    </div>
  );
}

export default TopBonusesBar;
